import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from 'recharts';
import Layout from '../components/Layout';
import { supabase } from '../config/supabase';
import DataImportExport from '../components/DataImportExport';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

export default function Reports() {
  const [timeRange, setTimeRange] = useState('month');
  const [membershipStats, setMembershipStats] = useState([]);
  const [ageGroupStats, setAgeGroupStats] = useState([]);
  const [genderStats, setGenderStats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, [timeRange]);

  const fetchStats = async () => {
    try {
      setLoading(true);

      // Fetch membership statistics
      const { data: memberships } = await supabase
        .from('contacts')
        .select('membership_status, created_at')
        .gte('created_at', getDateRange(timeRange));

      // Process membership data
      const membershipData = processTimeSeriesData(memberships, 'membership_status');
      setMembershipStats(membershipData);

      // Fetch age group statistics
      const { data: ageGroups } = await supabase
        .from('contacts')
        .select('age_group');

      const ageGroupData = processAgeGroupData(ageGroups);
      setAgeGroupStats(ageGroupData);

      // Fetch gender statistics
      const { data: genders } = await supabase
        .from('contacts')
        .select('gender');

      const genderData = processGenderData(genders);
      setGenderStats(genderData);

    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDateRange = (range) => {
    const now = new Date();
    switch (range) {
      case 'week':
        return new Date(now.setDate(now.getDate() - 7)).toISOString();
      case 'month':
        return new Date(now.setMonth(now.getMonth() - 1)).toISOString();
      case 'year':
        return new Date(now.setFullYear(now.getFullYear() - 1)).toISOString();
      default:
        return new Date(now.setMonth(now.getMonth() - 1)).toISOString();
    }
  };

  const processTimeSeriesData = (data, field) => {
    // Process data for time series chart
    const grouped = data.reduce((acc, item) => {
      const date = new Date(item.created_at).toLocaleDateString();
      acc[date] = acc[date] || { date };
      acc[date][item[field]] = (acc[date][item[field]] || 0) + 1;
      return acc;
    }, {});

    return Object.values(grouped);
  };

  const processAgeGroupData = (data) => {
    const grouped = data.reduce((acc, { age_group }) => {
      if (age_group) {
        acc[age_group] = (acc[age_group] || 0) + 1;
      }
      return acc;
    }, {});

    return Object.entries(grouped).map(([name, value]) => ({ name, value }));
  };

  const processGenderData = (data) => {
    const grouped = data.reduce((acc, { gender }) => {
      if (gender) {
        acc[gender] = (acc[gender] || 0) + 1;
      }
      return acc;
    }, {});

    return Object.entries(grouped).map(([name, value]) => ({ name, value }));
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 4 
        }}>
          <Typography variant="h4" component="h1">
            Reports & Analytics
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Time Range</InputLabel>
              <Select
                value={timeRange}
                label="Time Range"
                onChange={(e) => setTimeRange(e.target.value)}
              >
                <MenuItem value="week">Last Week</MenuItem>
                <MenuItem value="month">Last Month</MenuItem>
                <MenuItem value="year">Last Year</MenuItem>
              </Select>
            </FormControl>
            <DataImportExport />
          </Box>
        </Box>

        <Grid container spacing={3}>
          {/* Membership Growth Chart */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom>
                Membership Growth
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={membershipStats}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="Active" fill="#4caf50" />
                  <Bar dataKey="Inactive" fill="#ff9800" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Age Group Distribution */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom>
                Age Group Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={ageGroupStats}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label
                  >
                    {ageGroupStats.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Gender Distribution */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom>
                Gender Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={genderStats}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label
                  >
                    {genderStats.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Layout>
  );
}
